import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Button } from "@toddle-edu/ds-web";
import { EyeOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import ParticipantsModal from "../ParticipantsModal";
import classes from "./ParticipantsSummary.scss";
import { getIsStaff } from "modules/CommonSelectors";

/**
 * Compact participants summary row.
 * Shows a participant count title, a breakdown subtitle, and a "View" button.
 * Clicking "View" opens the ParticipantsModal.
 */
const ParticipantsSummary = ({
  totalCount,
  summaryText,
  eventTitle,
  testId,
  associatedEntities,
  sharedWith,
  staffCount,
  studentCount,
  familyCount,
  label,
  participantModalLabel,
  eventType,
}) => {
  const { t } = useI18n(["calendar", "common"]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isStaff = getIsStaff();

  const handleViewClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Don't show participants summary if user is not staff
  if (!isStaff) return null;

  return (
    <>
      <div className={classes.container} data-test-id={testId}>
        <div className={classes.textGroup}>
          <div className={classNames("text-label", classes.title)}>
            {label || t("calendar:participants_count", { count: totalCount })}
          </div>
          <div className={classNames("text-body", classes.summaryText)}>
            {summaryText}
          </div>
        </div>

        <Button
          variant="progressive"
          type="inline"
          dsVersion="2.0"
          onClick={handleViewClick}
          icon={<EyeOutlined />}
          testId={`${testId}-view-button`}
        >
          {t("common:view")}
        </Button>
      </div>

      {isModalOpen ? (
        <ParticipantsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          eventTitle={eventTitle}
          associatedEntities={associatedEntities}
          sharedWith={sharedWith}
          staffCount={staffCount}
          studentCount={studentCount}
          familyCount={familyCount}
          label={participantModalLabel || label}
          eventType={eventType}
        />
      ) : null}
    </>
  );
};

ParticipantsSummary.propTypes = {
  totalCount: PropTypes.number.isRequired,
  summaryText: PropTypes.string.isRequired,
  eventTitle: PropTypes.string,
  testId: PropTypes.string,
  associatedEntities: PropTypes.array,
  label: PropTypes.string,
  participantModalLabel: PropTypes.string,
  sharedWith: PropTypes.array,
  staffCount: PropTypes.number,
  studentCount: PropTypes.number,
  familyCount: PropTypes.number,
  eventType: PropTypes.string,
};

ParticipantsSummary.defaultProps = {
  eventTitle: "",
  testId: "participants-summary",
  associatedEntities: [],
  sharedWith: [],
  label: "",
  participantModalLabel: "",
  staffCount: 0,
  studentCount: 0,
  familyCount: 0,
  eventType: undefined,
};

export default ParticipantsSummary;
